import { Drawer } from 'antd'
import styled from 'styled-components'
import { Text, Button, Heading } from '@lmc/ui-kit/src/general'
import { Spinner, Edit } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { Tag } from 'antd/lib'
import { StockCheckDetailContent } from './StockCheckDetailContent'
import { InitialStockDetailContent } from './InitialStockDetailContent'
import { AddStockDetailContent } from './AddStockDetailContent'
import { DisposalDetailContent } from './DisposalDetailContent'
import { CommentDetailContent } from './CommentDetailContent'
import { AdditionalDetailsSection } from './AdditionalDetailsSection'
import { RevisionHistorySection } from '../../componentsV2/RevisionHistory/RevisionHistorySection'
import { MedicationStockReason } from '../../types'
import { getActionTitle, getActionIcon } from '../../utils/stockRecordUtils'
import { StockRecord } from '../../types'
import Comment from '../../iconsV2/Comment'

type StockRecordDrawerProps = {
  isVisible: boolean
  onClose: () => void
  record: StockRecord | undefined
  medicationName: string
  controlledDrug?: boolean
  onEdit?: (record: StockRecord, actionType: MedicationStockReason) => void
}

export const StockRecordDrawer = ({
  isVisible,
  onClose,
  record,
  medicationName,
  controlledDrug,
  onEdit
}: StockRecordDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isLoading = false

  if (!record) return null

  const isEventRecord = record.type === 'event'
  const actionType = record.action

  if (!isEventRecord && !actionType) return null

  const handleEdit = () => {
    if (onEdit && record && actionType) {
      onEdit(record, actionType)
    }
  }

  const titleText = isEventRecord
    ? t('COMMENT')
    : getActionTitle(actionType, t, record?.administrationStatus)

  const showEditButton =
    !isEventRecord &&
    actionType !== MedicationStockReason.MED_ADMIN &&
    actionType !== MedicationStockReason.INITIAL_STOCK

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && record && (
            <TitleWrapper>
              <MedicationNameRow>
                <MedicationNameText>{medicationName}</MedicationNameText>
                {controlledDrug && <Tag color="red">{t('CD')}</Tag>}
              </MedicationNameRow>
              <TitleContainer>
                {isEventRecord ? (
                  <IconWrapper>
                    <Comment />
                  </IconWrapper>
                ) : (
                  getActionIcon(actionType) && (
                    <IconWrapper>{getActionIcon(actionType)}</IconWrapper>
                  )
                )}
                <StyledTitleHeading level={4}>{titleText}</StyledTitleHeading>
              </TitleContainer>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && record && (
        <DrawerContent>
          <TopContent>
            <DetailsHeading level={3}>{t('DETAILS')}</DetailsHeading>
            {showEditButton && (
              <Button variant="text" onClick={handleEdit}>
                <Edit />
                {t('EDIT')}
              </Button>
            )}
          </TopContent>
          {actionType === MedicationStockReason.INITIAL_STOCK && (
            <>
              <InitialStockDetailContent
                record={record}
                medicationName={medicationName}
              />
              <AdditionalDetailsSection record={record} />
            </>
          )}
          {(actionType === MedicationStockReason.STOCK_COUNT ||
            actionType === MedicationStockReason.RECONCILIATION) && (
            <>
              <StockCheckDetailContent
                record={record}
                medicationName={medicationName}
              />
              <AdditionalDetailsSection record={record} />
            </>
          )}
          {actionType === MedicationStockReason.RESTOCK && (
            <>
              <AddStockDetailContent
                record={record}
                medicationName={medicationName}
              />
              <AdditionalDetailsSection record={record} />
            </>
          )}
          {actionType === MedicationStockReason.DISPOSAL && (
            <>
              <DisposalDetailContent
                record={record}
                medicationName={medicationName}
              />
              <AdditionalDetailsSection record={record} />
            </>
          )}
          {isEventRecord && (
            <>
              <CommentDetailContent record={record} />
              <AdditionalDetailsSection record={record} />
            </>
          )}
          <RevisionHistorySection
            changelog={record.changelog}
            isDisposal={actionType === MedicationStockReason.DISPOSAL}
            isRestock={actionType === MedicationStockReason.RESTOCK}
          />
        </DrawerContent>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledTitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const DrawerContent = styled.div``

const MedicationNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationNameText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 0 16px 0;
`

const DetailsHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
  margin: 0;
`
